import {
  FinanceEditDimensionValueDocument,
  FinanceGetDimensionValueDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface EditDimensionValueForm {
  id: number
  name: string
  code: string
  description: string
  legalEntityName: string
}

const useEditDimensionValue = (id: number, onClose: (success: boolean) => void) => {
  const { company } = useCurrentSession()
  const { data, error } = useQuery(FinanceGetDimensionValueDocument, {
    variables: {
      id,
    },
  })
  const loading = !data && !error
  const dimensionValue = data?.finance.customDimensionValue

  const [editDimensionValue, editRequest] = useMutation(FinanceEditDimensionValueDocument, {
    extractorFn: (editData) => editData.financeMutations.editFinanceCustomDimensionValue,
  })

  const form = useForm<EditDimensionValueForm>({
    defaultValues: {
      id: id,
      name: dimensionValue?.name || '',
      code: dimensionValue?.code || '',
      description: dimensionValue?.description || '',
      legalEntityName: dimensionValue?.legalEntity?.legalName || '',
    },

    validate: (newData) => {
      const errors: FormErrors<EditDimensionValueForm> = {}
      if (!newData.name) {
        errors.name = i18n.t('finance.dimension_detail.sidepanel.mandatory')
      }
      return errors
    },

    onSubmit: async (values) => {
      const variables = {
        id: id,
        name: values.name,
        code: values.code,
        description: values.description,
        companyId: company.id,
      }

      const result = await editDimensionValue({ variables })

      const handled = handleResult(result, (errors) => {
        form.setErrors({
          name: errors['name'],
          code: errors['code'],
          description: errors['description'],
        })
      })

      if (handled.success) {
        onClose(true)
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.dimension_detail.sidepanel.success'),
        })
      }
      return handled
    },
  })

  useUnsafeEffect(() => {
    if (!loading && dimensionValue) {
      const values = {
        id: id,
        name: dimensionValue.name,
        description: dimensionValue.description || '',
        code: dimensionValue.code || '',
        legalEntityName: dimensionValue.legalEntity?.legalName || '',
      }

      form.setValues(values)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, dimensionValue, id]) // form is not stable

  return {
    loading,
    saving: editRequest.loading,
    hasErrors: Object.values(form.errors).filter((e) => !!e).length > 0,
    form,
    dimensionValue,
  }
}

export default useEditDimensionValue
